export interface ImageDimensions { width: number; height: number }

export const CLIENT_LIMITS = {
  anonymous: { bytes: 5 * 1024 * 1024, pixels: 25_000_000 },
  free: { bytes: 20 * 1024 * 1024, pixels: 50_000_000 },
  paid: { bytes: 20 * 1024 * 1024, pixels: 100_000_000 },
  superuser: { bytes: 20 * 1024 * 1024, pixels: 100_000_000 },
} as const;

export async function decodeDimensions(file: File): Promise<ImageDimensions> {
  const bitmap = await createImageBitmap(file);
  const dimensions = { width: bitmap.width, height: bitmap.height };
  bitmap.close();
  return dimensions;
}

export async function validateClientImage(file: File, plan: keyof typeof CLIENT_LIMITS): Promise<ImageDimensions> {
  if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
    throw new Error("Use a PNG, JPEG, or WebP image.");
  }
  const limits = CLIENT_LIMITS[plan];
  if (file.size > limits.bytes) throw new Error(`This plan accepts images up to ${limits.bytes / 1024 / 1024} MB.`);
  const dimensions = await decodeDimensions(file).catch(() => {
    throw new Error("This image could not be decoded.");
  });
  if (dimensions.width * dimensions.height > limits.pixels) {
    throw new Error(`This plan accepts images up to ${limits.pixels / 1_000_000} megapixels.`);
  }
  return dimensions;
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export async function saveBlob(blob: Blob, filename: string): Promise<void> {
  const file = new File([blob], filename, { type: blob.type });
  const shareData = { files: [file], title: filename };
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isIos && navigator.canShare?.(shareData)) {
    await navigator.share(shareData);
    return;
  }
  downloadBlob(blob, filename);
}
